import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, RefreshCw, GitBranch, Database, Cpu, Globe,
  CheckCircle2, AlertCircle, XCircle, PlayCircle, Clock, Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { showFriendlyError } from '../utils/friendlyErrors';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Optional "other env" URL, admins can paste another deployment URL
// (e.g., the production domain when viewing preview, or vice versa)
// to compare side-by-side. Stored only in component state.
const OTHER_ENV_CHOICES = [
  { label: 'Preview (lower env)', url: 'https://futurepath-hs-demo.preview.emergentagent.com' },
  { label: 'Production (agenticfuturepath.com)', url: 'https://agenticfuturepath.com' },
];

export default function AdminDeploymentStatusPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [local, setLocal] = useState(null);
  const [remote, setRemote] = useState(null);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const fetchLocal = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/deployment-status`, {
        ...getAuthHeader(),
        withCredentials: true,
      });
      setLocal(res.data);
    } catch (err) {
      showFriendlyError(err, { context: 'Deployment status' });
    }
  }, [getAuthHeader]);

  const fetchRemote = useCallback(async (url) => {
    if (!url) {
      setRemote(null);
      return;
    }
    try {
      // /api/deployment-info is public (no auth), so we can curl it directly
      // from any environment to compare.
      const res = await axios.get(`${url.replace(/\/+$/, '')}/api/deployment-info`);
      setRemote({ ...res.data, _source_url: url });
    } catch (err) {
      toast.error(`Could not reach ${url}: ${err.message}`);
      setRemote(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'super_admin', 'org_admin'].includes(user.role)) {
      toast.error('Admin access required');
      navigate('/app/dashboard');
      return;
    }
    setLoading(true);
    fetchLocal().finally(() => setLoading(false));
  }, [user, navigate, fetchLocal]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLocal();
    if (remoteUrl) await fetchRemote(remoteUrl);
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleTriggerJob = async () => {
    if (!window.confirm(
      'Run the daily data refresh now?\n\n' +
      'This is ADDITIVE, it inserts new scholarships, deactivates past ' +
      'deadlines, verifies federal loan rules, and archives past exam dates. ' +
      'It will NOT delete any existing rows.'
    )) return;
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/admin/data-refresh/trigger`, {}, {
        ...getAuthHeader(),
        withCredentials: true,
      });
      const s = res.data.summary || {};
      toast.success(
        `Refresh done, +${s.scholarships?.scholarships_inserted || 0} scholarships, ` +
        `${s.scholarships?.scholarships_deactivated || 0} deactivated, ` +
        `${s.exam_dates?.exam_dates_archived || 0} exams archived`
      );
      await fetchLocal();
    } catch (err) {
      showFriendlyError(err, { context: 'Run refresh' });
    } finally {
      setTriggering(false);
    }
  };

  const compareCommit = local?.git?.commit_full && remote?.git_commit_full &&
    local.git.commit_full === remote.git_commit_full;
  const sameDb = local?.environment?.db_name && remote?.db_name &&
    local.environment.db_name === remote.db_name;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1729] text-white flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1729] text-white" data-testid="admin-deployment-status-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Deployment Status</h1>
              <p className="text-sm text-slate-300">
                Verify what code & data is live in this environment and compare against another deploy.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="border-slate-700"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleTriggerJob}
              disabled={triggering}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="trigger-refresh-btn"
            >
              <PlayCircle className="w-4 h-4 mr-1" />
              {triggering ? 'Running…' : 'Run Daily Refresh Now'}
            </Button>
          </div>
        </div>

        {/* Compare with another environment */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-300 mr-2">Compare against:</span>
            {OTHER_ENV_CHOICES.map((c) => (
              <button
                key={c.url}
                onClick={() => { setRemoteUrl(c.url); fetchRemote(c.url); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  remoteUrl === c.url
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-300'
                    : 'bg-slate-800 border-slate-700 hover:bg-slate-700'
                }`}
                data-testid={`compare-${c.label.split(' ')[0].toLowerCase()}-btn`}
              >
                {c.label}
              </button>
            ))}
            {remoteUrl && (
              <button
                onClick={() => { setRemoteUrl(''); setRemote(null); }}
                className="px-3 py-1.5 rounded-lg text-xs text-slate-300 hover:text-white"
                data-testid="clear-compare-btn"
              >
                Clear
              </button>
            )}
          </div>
          {remote && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="flex items-center gap-1">
                {compareCommit
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  : <XCircle className="w-4 h-4 text-red-400" />}
                <span className="text-slate-300">Same commit</span>
              </span>
              <span className="flex items-center gap-1">
                {sameDb
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  : <AlertCircle className="w-4 h-4 text-amber-400" />}
                <span className="text-slate-300">Same DB</span>
              </span>
            </div>
          )}
        </div>

        {/* Two-column side-by-side */}
        <div className={`grid ${remote ? 'lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
          <EnvCard
            title="This Environment"
            badge="local"
            git={local?.git}
            env={local?.environment}
            collections={local?.collections}
            frontend={local?.frontend}
            lastRefresh={local?.last_data_refresh}
            testidPrefix="local"
          />
          {remote && (
            <EnvCard
              title={`Remote (${remoteUrl.replace(/^https?:\/\//, '')})`}
              badge="remote"
              git={{
                commit: remote.git_commit,
                commit_full: remote.git_commit_full,
                branch: remote.git_branch,
                time: remote.git_last_commit_time,
                msg: remote.git_last_commit_msg,
              }}
              env={{
                db_name: remote.db_name,
                frontend_url: remote.frontend_url,
                python_version: remote.python_version,
                seed_mode: remote.seed_mode,
              }}
              collections={remote.collections}
              frontend={{
                build_present: remote.frontend_build_present,
                bundle: remote.frontend_bundle,
              }}
              lastRefresh={remote.last_data_refresh}
              testidPrefix="remote"
            />
          )}
        </div>

        {/* Recent refresh logs */}
        <div className="mt-8 bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid="refresh-logs-section">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Recent Data Refresh Runs
          </h2>
          {local?.recent_refresh_logs?.length ? (
            <div className="space-y-2">
              {local.recent_refresh_logs.map((log, i) => (
                <div key={log.id || i} className="bg-slate-800/40 rounded-lg p-3 text-sm">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      log.trigger === 'manual' ? 'bg-blue-600/20 text-blue-300' : 'bg-slate-700 text-slate-300'
                    }`}>
                      {log.trigger}
                    </span>
                    <span className="text-slate-300">{new Date(log.started_at).toLocaleString()}</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-slate-300">{log.duration_seconds}s</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-emerald-400">
                      +{log.scholarships?.scholarships_inserted || 0} new
                    </span>
                    <span className="text-amber-400">
                      ·  {log.scholarships?.scholarships_deactivated || 0} deactivated
                    </span>
                    <span className="text-slate-300">
                      ·  {log.exam_dates?.exam_dates_archived || 0} exams archived
                    </span>
                    {(log.scholarships?.errors?.length || log.federal_loan_rates?.errors?.length || log.exam_dates?.errors?.length) ? (
                      <span className="text-red-400 text-xs">errors present</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-slate-300 text-sm">
              No refresh runs logged yet. Click <strong>Run Daily Refresh Now</strong> above to create your first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EnvCard({ title, badge, git, env, collections, frontend, lastRefresh, testidPrefix }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid={`${testidPrefix}-env-card`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${
          badge === 'local' ? 'bg-emerald-600/20 text-emerald-300' : 'bg-blue-600/20 text-blue-300'
        }`}>
          {badge}
        </span>
      </div>

      {/* Git */}
      <Section icon={<GitBranch className="w-4 h-4 text-purple-400" />} title="Git">
        <Row label="Commit" value={
          <code className="font-mono text-xs" data-testid={`${testidPrefix}-git-commit`}>
            {git?.commit || '-'}
          </code>
        } />
        <Row label="Branch" value={git?.branch || '-'} />
        <Row label="Last commit" value={git?.time ? new Date(git.time).toLocaleString() : '-'} />
        <Row label="Message" value={
          <span className="text-xs text-slate-300 italic">{git?.msg || '-'}</span>
        } wrap />
      </Section>

      {/* Environment */}
      <Section icon={<Cpu className="w-4 h-4 text-blue-400" />} title="Environment">
        <Row label="DB" value={env?.db_name || '-'} />
        <Row label="Frontend URL" value={
          <span className="text-xs text-slate-300 break-all">{env?.frontend_url || '-'}</span>
        } wrap />
        <Row label="Python" value={env?.python_version || '-'} />
        <Row label="Seed mode" value={
          <span className="text-emerald-400 text-xs">{env?.seed_mode || '-'}</span>
        } />
      </Section>

      {/* Frontend bundle */}
      <Section icon={<Globe className="w-4 h-4 text-cyan-400" />} title="Frontend bundle">
        {frontend?.build_present ? (
          <Row label="main.js" value={
            <code className="font-mono text-xs text-slate-300" data-testid={`${testidPrefix}-bundle`}>
              {frontend?.bundle || '-'}
            </code>
          } />
        ) : (
          <div className="text-amber-400 text-xs">
            No /frontend/build/asset-manifest.json, likely served from dev or another build path.
          </div>
        )}
      </Section>

      {/* Collections */}
      <Section icon={<Database className="w-4 h-4 text-emerald-400" />} title="Collection counts">
        {collections && !collections.error ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            {Object.entries(collections).map(([k, v]) => (
              <div key={k} className="flex justify-between" data-testid={`${testidPrefix}-coll-${k}`}>
                <span className="text-slate-300 truncate">{k}</span>
                <span className="font-mono">{v}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-red-400 text-xs">{collections?.error || 'unavailable'}</div>
        )}
      </Section>

      {/* Last refresh */}
      <Section icon={<Clock className="w-4 h-4 text-amber-400" />} title="Last data refresh">
        {lastRefresh ? (
          <div className="space-y-1 text-sm">
            <Row label="When" value={new Date(lastRefresh.started_at).toLocaleString()} />
            <Row label="Trigger" value={lastRefresh.trigger} />
            <Row label="Duration" value={`${lastRefresh.duration_seconds}s`} />
            <Row label="Scholarships +" value={lastRefresh.scholarships?.scholarships_inserted || 0} />
            <Row label="Scholarships deactivated" value={lastRefresh.scholarships?.scholarships_deactivated || 0} />
            <Row label="Exams archived" value={lastRefresh.exam_dates?.exam_dates_archived || 0} />
          </div>
        ) : (
          <div className="text-slate-300 text-xs">Never (job not yet run in this environment)</div>
        )}
      </Section>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center gap-2 mb-1.5 text-slate-300 font-medium text-sm">
        {icon} {title}
      </div>
      <div className="bg-slate-800/30 rounded-lg p-3 space-y-1">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value, wrap = false }) {
  return (
    <div className={`flex ${wrap ? 'flex-col' : 'justify-between items-center'} gap-1 text-sm`}>
      <span className="text-slate-300">{label}</span>
      <span className="text-slate-200">{value}</span>
    </div>
  );
}
